import React, { useEffect, useState } from "react";
import fetchData from "../../api/fetchApi";
import { getLang } from "../../i18n";

export default function ChatPage() {
  const [filterText, setFilterText] = useState("");
  const lang = getLang();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const endpoint = `/messages?page=${currentPage}&per_page=${perPage}`;
    fetchData(endpoint)
      .then((data) => {
        setMessages(Array.isArray(data.messages) ? data.messages : []);
        setTotal(data.total || 0);
        setPerPage(data.per_page || 10);
        setLoading(false);
      })
      .catch((err) => {
        if (err.status === 404) {
          setMessages([]);
          setTotal(0);
        } else {
          setError(err.message);
        }
        setLoading(false);
      });
  }, [currentPage, perPage]);

  const totalPages = Math.max(Math.ceil(total / perPage), 1);
  const normalizedFilter = filterText.trim().toLowerCase();
  const filteredMessages = messages.filter((message) =>
    [message.to_number, message.body, message.type]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedFilter))
  );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div
      className="panel-card mx-auto w-full max-w-[1440px]"
      dir="rtl"
    >
      <h2 className="mb-5 text-2xl font-semibold tracking-tight text-slate-950">
        إدارة الرسائل
      </h2>

      <div className="mx-auto ">
        {loading ? (
          <p className="text-blue-500 text-lg font-medium">
            جاري تحميل الرسائل...
          </p>
        ) : error ? (
          <p className="text-red-600 font-semibold">{error}</p>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
              <div className="text-sm text-gray-700 ">
                عدد البيانات التى تم العثور عليها:{" "}
                <span className="text-teal-700 font-bold">{normalizedFilter ? filteredMessages.length : total}</span>
              </div>
              <input
                type="text"
                placeholder="بحث"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                className="border rounded px-3 py-1 w-full sm:w-1/3 focus:outline-none bg-gray-50  "
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] text-right border-collapse">
                <thead>
                  <tr className="bg-gray-100 ">
                    <th className="py-2 px-3 text-gray-700 ">
                      رقم المستلم
                    </th>
                    <th className="py-2 px-3 text-gray-700 ">
                      نص الرسالة
                    </th>
                    <th className="py-2 px-3 text-gray-700 ">
                      الحالة
                    </th>
                    <th className="py-2 px-3 text-gray-700 ">
                      النوع
                    </th>
                    <th className="py-2 px-3 text-gray-700 ">
                      التاريخ والوقت
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMessages.length === 0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        className="py-6 px-4 text-center text-gray-500"
                      >
                        لا توجد رسائل.
                      </td>
                    </tr>
                  ) : (
                    filteredMessages.map((msg, idx) => (
                      <tr
                        key={msg.uuid || idx}
                        className={`border-t `}
                      >
                        <td className="py-2 px-3 text-blue-600 underline cursor-pointer">
                          {msg.to_number}
                        </td>
                        <td className="py-2 px-3 ">
                          {msg.body}
                        </td>
                        <td className="py-2 px-3 ">
                          {String(msg.status) === "-1" ? (
                            <span className="text-red-600">فشل</span>
                          ) : String(msg.status) === "0" ? (
                            <span className="text-yellow-600">قيد الإرسال</span>
                          ) : String(msg.status) === "1" ? (
                            <span className="text-green-600">تم الإرسال</span>
                          ) : String(msg.status) === "2" ? (
                            <span className="text-orange-600">
                                     تم الاستلام
                      </span>
                          ) : String(msg.status) === "3" ? (
                            <span className="text-blue-600">
                              تم القراءة
                            </span>
                          ) : (
                            <span className="text-gray-500">غير معروف</span>
                          )}
                        </td>
                        <td className="py-2 px-3 ">
                          {msg.type || "-"}
                        </td>
                        <td className="py-2 px-3 ">
                          {msg.created_at
                            ? new Date(msg.created_at).toLocaleDateString(
                               lang === "ar" ? "ar-EG" : "en-US",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                }
                              ) +
                              " " +
                              new Date(msg.created_at).toLocaleTimeString(
                                lang === "ar" ? "ar-EG" : "en-US",
                                { hour: "2-digit", minute: "2-digit" }
                              )
                            : "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <nav
              className="flex items-center justify-between pt-4"
              aria-label="Table navigation"
            >
              <span className="text-sm font-normal text-gray-500">
                عرض
                <span className="font-semibold mx-1 text-gray-900">
                  {messages.length === 0 ? 0 : (currentPage - 1) * perPage + 1}-
                  {Math.min(currentPage * perPage, total)}
                </span>
                من
                <span className="font-semibold mx-1 text-gray-900 ">{total}</span>
                رسالة
              </span>
              <ul className="inline-flex space-x-px text-sm h-8">
                <li>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center justify-center px-3 h-8 mx-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    السابق
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                  )
                  .map((page, idx, arr) => (
                    <li key={page} className="flex items-center">
                      {idx > 0 && page - arr[idx - 1] > 1 ? (
                        <span className="flex items-center justify-center px-2 h-8 leading-tight text-gray-500 bg-white border border-gray-300">
                          ...
                        </span>
                      ) : null}
                      <button
                        onClick={() => handlePageChange(page)}
                        className={`flex items-center justify-center px-3 h-8 leading-tight border border-gray-300
                          ${
                            page === currentPage
                              ? "text-white bg-blue-700 border-blue-700"
                              : "text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700"
                          }`}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                <li>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages || total === 0}
                    className="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    التالي
                  </button>
                </li>
              </ul>
            </nav>
          </>
        )}
      </div>
    </div>
  );
}
